
import _ from 'lodash';
import type { Dispatch } from 'redux';

import { getCurrentConference } from '../conference/functions';
import MeetHourJS, { MHConnectionEvents } from '../lib-meet-hour';
import {
    getBackendSafeRoomName,
    parseURIString
} from '../util';

import {
    CONNECTION_DISCONNECTED,
    CONNECTION_ESTABLISHED,
    CONNECTION_FAILED,
    CONNECTION_WILL_CONNECT,
    SET_LOCATION_URL
} from './actionTypes';
import { MH_CONNECTION_URL_KEY } from './constants';
import logger from './logger';

/**
 * The error structure passed to the {@link connectionFailed} action.
 *
 * Note there was an intention to make the error resemble an Error instance (to
 * the extent that meet-hour needs it).
 */
export type ConnectionFailedError = {

    /**
     * The invalid credentials that were used to authenticate and the
     * authentication failed.
     */
    credentials?: {

        /**
         * The XMPP user's ID.
         */
        jid: string,

        /**
         * The XMPP user's password.
         */
        password: string
    },

    /**
     * The details about the connection failed event.
     */
    details?: Object,

    /**
     * Error message.
     */
    message?: string,

    /**
     * One of {@link MHConnectionError} constants (defined in
     * lib-meet-hour).
     */
    name: string,

    /**
     * Indicates whether this event is recoverable or not.
     */
    recoverable?: boolean
};

/**
 * Opens new connection.
 *
 * @param {string} [id] - The XMPP user's ID (e.g. {@code user@server.com}).
 * @param {string} [password] - The XMPP user's password.
 * @returns {Function}
 */
export function connect(id: ?string, password: ?string) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();
        const options = _constructOptions(state);
        const { locationURL } = state['features/base/connection'];
        const { jwt } = state['features/base/jwt'];
        const connection = new MeetHourJS.MHConnection(options.appId, jwt, options);

        connection[MH_CONNECTION_URL_KEY] = locationURL;

        dispatch(_connectionWillConnect(connection));

        connection.addEventListener(
            MHConnectionEvents.CONNECTION_DISCONNECTED,
            _onConnectionDisconnected);
        connection.addEventListener(
            MHConnectionEvents.CONNECTION_ESTABLISHED,
            _onConnectionEstablished);
        connection.addEventListener(
                MHConnectionEvents.CONNECTION_REDIRECTED,
                _onConnectionRedirected);
        connection.addEventListener(
            MHConnectionEvents.CONNECTION_FAILED,
            _onConnectionFailed);

        connection.connect({
            id,
            password
        });

        /**
         * Dispatches {@code CONNECTION_DISCONNECTED} action when connection is
         * disconnected.
         *
         * @private
         * @returns {void}
         */
        function _onConnectionDisconnected() {
            unsubscribe();
            dispatch(connectionDisconnected(connection));
        }

        /**
         * Resolves external promise when connection is established.
         *
         * @private
         * @returns {void}
         */
        function _onConnectionEstablished() {
            connection.removeEventListener(
                MHConnectionEvents.CONNECTION_ESTABLISHED,
                _onConnectionEstablished);
            dispatch(connectionEstablished(connection, Date.now()));
        }


        /**
             * Connection was redirected.
             *
             * @param {string|undefined} vnode - The vnode to connect to.
             * @param {string} focusJid - The focus jid to use.
             * @param {string|undefined} username - The username to use when joining. This is after promotion from
             * visitor to main participant.
             * @param {string|undefined} role - The main-meeting role granted with the promotion.
             * @private
             * @returns {void}
             */
        function _onConnectionRedirected(vnode, focusJid, username, role) {
            connection.removeEventListener(MHConnectionEvents.CONNECTION_REDIRECTED, _onConnectionRedirected);

            // There is no global APP on React Native; APP.store here was a ReferenceError on every redirect.
            dispatch(require('../conference/actions').redirect(vnode, focusJid, username, role));
        }


        /**
         * Rejects external promise when connection fails.
         *
         * @param {MHConnectionErrors} err - Connection error.
         * @param {string} [msg] - Error message supplied by lib-meet-hour.
         * @param {Object} [credentials] - The invalid credentials that were
         * used to authenticate and the authentication failed.
         * @param {string} [credentials.jid] - The XMPP user's ID.
         * @param {string} [credentials.password] - The XMPP user's password.
         * @param {Object} details - Additional information about the error.
         * @private
         * @returns {void}
         */
        function _onConnectionFailed(
                err: string,
                msg: string,
                credentials: Object,
                details: Object) {
            unsubscribe();
            dispatch(
                connectionFailed(
                    connection, {
                        credentials,
                        details,
                        name: err,
                        message: msg
                    }
                ));
        }

        /**
         * Unsubscribes every listener this action attached to the connection instance. The established and
         * redirected listeners only remove themselves when their event fires, so a connection that fails or ends
         * first would otherwise keep them.
         *
         * @returns {void}
         */
        function unsubscribe() {
            connection.removeEventListener(
                MHConnectionEvents.CONNECTION_DISCONNECTED,
                _onConnectionDisconnected);
            connection.removeEventListener(
                MHConnectionEvents.CONNECTION_ESTABLISHED,
                _onConnectionEstablished);
            connection.removeEventListener(
                MHConnectionEvents.CONNECTION_REDIRECTED,
                _onConnectionRedirected);
            connection.removeEventListener(
                MHConnectionEvents.CONNECTION_FAILED,
                _onConnectionFailed);
        }
    };
}

/**
 * Create an action for when the signaling connection has been lost.
 *
 * @param {MHConnection} connection - The {@code MHConnection} which
 * disconnected.
 * @private
 * @returns {{
 *     type: CONNECTION_DISCONNECTED,
 *     connection: MHConnection
 * }}
 */
export function connectionDisconnected(connection: Object) {
    return {
        type: CONNECTION_DISCONNECTED,
        connection
    };
}

/**
 * Create an action for when the signaling connection has been established.
 *
 * @param {MHConnection} connection - The {@code MHConnection} which was
 * established.
 * @param {number} timeEstablished - The time at which the
 * {@code MHConnection} which was established.
 * @public
 * @returns {{
 *     type: CONNECTION_ESTABLISHED,
 *     connection: MHConnection,
 *     timeEstablished: number
 * }}
 */
export function connectionEstablished(
        connection: Object, timeEstablished: number) {
    return {
        type: CONNECTION_ESTABLISHED,
        connection,
        timeEstablished
    };
}

/**
 * Create an action for when the signaling connection could not be created.
 *
 * @param {MHConnection} connection - The {@code MHConnection} which
 * failed.
 * @param {ConnectionFailedError} error - Error.
 * @public
 * @returns {{
 *     type: CONNECTION_FAILED,
 *     connection: MHConnection,
 *     error: ConnectionFailedError
 * }}
 */
export function connectionFailed(
        connection: Object,
        error: ConnectionFailedError) {
    const { credentials } = error;

    if (credentials && !Object.keys(credentials).length) {
        error.credentials = undefined;
    }

    return {
        type: CONNECTION_FAILED,
        connection,
        error
    };
}

/**
 * Create an action for when a connection will connect.
 *
 * @param {MHConnection} connection - The {@code MHConnection} which will
 * connect.
 * @private
 * @returns {{
 *     type: CONNECTION_WILL_CONNECT,
 *     connection: MHConnection
 * }}
 */
function _connectionWillConnect(connection) {
    return {
        type: CONNECTION_WILL_CONNECT,
        connection
    };
}

/**
 * Constructs options to be passed to the constructor of {@code MHConnection}
 * based on the redux state.
 *
 * @param {Object} state - The redux state.
 * @returns {Object} The options to be passed to the constructor of
 * {@code MHConnection}.
 */
function _constructOptions(state) {
    // Deep clone the options to make sure we don't modify the object in the
    // redux store.
    const options = _.cloneDeep(state['features/base/config']);
    const { preferVisitor } = state['features/base/connection'];

    // A demotion asks jicofo for a viewer node. Without this the reconnect
    // joins the main room again.
    if (preferVisitor) {
        options.preferVisitor = true;
    }

    // Normalize the BOSH URL.
    let { bosh } = options;

    if (bosh) {
        const { locationURL } = state['features/base/connection'];

        if (bosh.startsWith('//')) {
            // By default our config.js doesn't include the protocol.
            bosh = `${locationURL.protocol}${bosh}`;
        } else if (bosh.startsWith('/')) {
            // Handle relative URLs, which won't work on mobile.
            const {
                protocol,
                host,
                contextRoot
            } = parseURIString(locationURL.href);


            bosh = `${protocol}//${host}${contextRoot || '/'}${bosh.substr(1)}`;
        }

        // Append room to the URL's search.
        const { room } = state['features/base/conference'];

        room && (bosh += `?room=${getBackendSafeRoomName(room)}`);

        // FIXME Remove deprecated 'bosh' option assignment at some point.
        options.serviceUrl = options.bosh = bosh;
    }

    return options;
}

/**
 * Closes connection.
 *
 * @returns {Function}
 */
export function disconnect() {
    return (dispatch: Dispatch<any>, getState: Function): Promise<void> => {
        const { conferenceLeft, conferenceWillLeave } = require('../conference/actions');
        const state = getState();

        // The conference we have already joined or are joining.
        const conference_ = getCurrentConference(state);

        // Promise which completes when the conference has been left and the
        // connection has been disconnected.
        let promise;

        // Leave the conference.
        if (conference_) {
            // In a fashion similar to MHConference's CONFERENCE_LEFT event
            // (and the respective Redux action) which is fired after the
            // conference has been left, notify the application about the
            // intention to leave the conference.
            dispatch(conferenceWillLeave(conference_));

            promise
                = conference_.leave()
                    .catch(error => {
                        logger.warn(
                            'MHConference.leave() rejected with:',
                            error);

                        // The library lib-meet-hour failed to make the
                        // MHConference leave. Which may be because
                        // MHConference thinks it has already left.
                        // Regardless of the failure reason, continue in
                        // meet-hour as if the leave has succeeded.
                        dispatch(conferenceLeft(conference_));
                    });
        } else {
            promise = Promise.resolve();
        }

        // Disconnect the connection.
        const { connecting, connection } = state['features/base/connection'];

        // The connection we have already connected or are connecting.
        const connection_ = connection || connecting;

        if (connection_) {
            promise = promise.then(() => connection_.disconnect());
        } else {
            //  logger.info('No connection found while disconnecting.');
        }

        return promise;
    };
}

/**
 * Sets the location URL of the application, connecton, conference, etc.
 *
 * @param {URL} [locationURL] - The location URL of the application,
 * connection, conference, etc.
 * @returns {{
 *     type: SET_LOCATION_URL,
 *     locationURL: URL
 * }}
 */
export function setLocationURL(locationURL: ?URL) {
    return {
        type: SET_LOCATION_URL,
        locationURL
    };
}
